// src/components/resources/tools/generators/SocialMediaMockup/socialMediaConfigUtils.ts
//
// Pure config-merge / draft-serialization helpers extracted from
// SocialMediaMockupGenerator. Kept framework-agnostic so they can be unit
// tested in isolation and reused by the persistence layer.

import type { SocialMediaConfig, SocialPlatform } from './types'

/**
 * Merge `initialConfig` into `DEFAULT_CONFIG`, preserving nested
 * `metrics` and `interactions` default fields when the caller only
 * overrides a subset. A naive shallow spread would replace the whole
 * nested object — invoking the rendering code with
 * `config.metrics.comments === undefined` and crashing formatMetric.
 */
export function mergeSocialMediaConfig(
  base: SocialMediaConfig,
  override: Partial<SocialMediaConfig> | undefined,
): SocialMediaConfig {
  if (!override) return base
  return {
    ...base,
    ...override,
    metrics: { ...base.metrics, ...(override.metrics ?? {}) },
    interactions: { ...base.interactions, ...(override.interactions ?? {}) },
  }
}

/**
 * Strips uploaded media data URLs before localStorage persistence (quota-safe).
 * When a variant page pins `platform` via `initialConfig`, the platform field is
 * omitted so a prior umbrella-session platform choice cannot override the variant.
 *
 * @param {SocialMediaConfig} value - Live mockup config.
 * @param {SocialPlatform} [pinnedPlatform] - Platform locked by a landing variant.
 * @returns {Partial<SocialMediaConfig>} Serializable draft fields.
 */
export function serializeSocialMediaDraft(
  value: SocialMediaConfig,
  pinnedPlatform?: SocialPlatform,
): Partial<SocialMediaConfig> {
  const { avatarUrl: _avatar, mediaUrl: _media, platform, ...rest } = {
    ...value,
    avatarUrl: undefined,
    mediaUrl: undefined,
  }
  if (pinnedPlatform) return rest
  return { ...rest, platform }
}

/**
 * Merges a stored draft into the live config, preserving a variant-pinned platform.
 *
 * @param {SocialMediaConfig} base - Route boot config (includes pinned platform).
 * @param {Partial<SocialMediaConfig> | null} stored - Parsed localStorage draft.
 * @param {SocialPlatform} [pinnedPlatform] - Platform locked by a landing variant.
 * @returns {SocialMediaConfig} Merged config.
 */
export function mergeStoredSocialDraft(
  base: SocialMediaConfig,
  stored: Partial<SocialMediaConfig> | null,
  pinnedPlatform?: SocialPlatform,
): SocialMediaConfig {
  if (!stored) return base
  if (pinnedPlatform) {
    const { platform: _ignored, ...storedWithoutPlatform } = stored
    return mergeSocialMediaConfig(base, storedWithoutPlatform)
  }
  return mergeSocialMediaConfig(base, stored)
}
